import { useMemo } from 'react';
import styled from 'styled-components';

import { GhostButton, BUTTON_SIZES } from 'ui/button';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { Menu } from 'ui/menu';
import { spacing } from 'ui/theme';

import { FILTERS } from '../constants';
import { useEventHistoryFiltersContext } from '../contexts/EventHistoryFiltersContext';
import {
  createSubMenuForEvents,
  createUserAgentSubMenu,
  getOptionIdForBrowser,
  getOptionIdForDevice,
  getOptionIdForEventType,
  getOptionIdForOperatingSystem,
  stripPrefix,
} from '../utils';

const DISPLAY_CONTENT = {
  clear: 'Clear',
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledGhostButton = styled(GhostButton)`
  margin-right: ${spacing.half};
`;

const StyledMenu = styled(Menu)`
  z-index: 3;
`;

export const FiltersMenu = () => {
  const {
    browserFilters,
    clearFilters,
    deviceFilters,
    eventFilters,
    operatingSystemFilters,
    isLoadingEvents,
    isLoadingUserAgents,
    events,
    browsers,
    operatingSystems,
    devices,
    toggleFilterForBrowser,
    toggleFilterForDevice,
    toggleFilterForEvent,
    toggleFilterForOperatingSystem,
  } = useEventHistoryFiltersContext();
  const subMenus = useMemo(
    () => [
      createSubMenuForEvents(events, isLoadingEvents),
      createUserAgentSubMenu(FILTERS.BROWSER, browsers, isLoadingUserAgents),
      createUserAgentSubMenu(FILTERS.OPERATING_SYSTEM, operatingSystems, isLoadingUserAgents),
      createUserAgentSubMenu(FILTERS.DEVICE, devices, isLoadingUserAgents),
    ],
    [isLoadingEvents, events, isLoadingUserAgents, browsers, operatingSystems, devices],
  );

  const selectedOptionIds = [
    ...browserFilters.map(getOptionIdForBrowser),
    ...deviceFilters.map(getOptionIdForDevice),
    ...eventFilters.map(getOptionIdForEventType),
    ...operatingSystemFilters.map(getOptionIdForOperatingSystem),
  ];
  const handleSelect = (optionId: string) => {
    const [filter, value] = stripPrefix(optionId);
    switch (filter) {
      case FILTERS.BROWSER:
        return toggleFilterForBrowser(value);
      case FILTERS.DEVICE:
        return toggleFilterForDevice(value);
      case FILTERS.EVENT_TYPE:
        return toggleFilterForEvent(value);
      case FILTERS.OPERATING_SYSTEM:
        return toggleFilterForOperatingSystem(value);
      default:
    }
  };

  return (
    <Wrapper>
      {!!selectedOptionIds.length && (
        <StyledGhostButton onClick={clearFilters} tabIndex={0} size={BUTTON_SIZES.LARGE}>
          {DISPLAY_CONTENT.clear}
        </StyledGhostButton>
      )}
      <StyledMenu
        id="event-history-filters"
        options={subMenus}
        defaultSelectedOptionIds={selectedOptionIds}
        handleSelectionChange={handleSelect}
      >
        <FilterMenuTriggerButton selectedFilterCount={selectedOptionIds.length} />
      </StyledMenu>
    </Wrapper>
  );
};
